import type React from 'react';
import classNames from 'classnames';
import Markdown from 'markdown-to-jsx';
import styles from './ProductBannerBox.module.scss';
import Picture from '../Picture/Picture';
import Source from '../Picture/Source';
import type { Image as ImageData } from '../Components/ComponentTypes';
import {
  PRODUCT_BANNER_BOX_LAYOUT_FULL,
  PRODUCT_BANNER_BOX_LAYOUT_HALF,
} from '../Components/ComponentTypes';
import { useWithStaticUrl } from '../WebshopContext/WebshopContext';

interface Props {
  component: ImageData;
}

const ProductBannerBox: React.FC<Props> = ({ component }) => {
  const withStaticUrl = useWithStaticUrl();

  if (!component.imageUri) {
    return null;
  }

  return (
    <div
      className={classNames(styles.ProductBannerBox, {
        [styles.ProductBannerBox_dark as string]: !!component.text,
        [`is-col-2 ${styles.ProductBannerBox_half}`]:
          component.layout === PRODUCT_BANNER_BOX_LAYOUT_HALF,
        [styles.ProductBannerBox_full as string]:
          component.layout === PRODUCT_BANNER_BOX_LAYOUT_FULL,
      })}
    >
      <div className={styles.ProductBannerBox__stack}>
        <div className={styles.ProductBannerBox__image}>
          {component.layout === PRODUCT_BANNER_BOX_LAYOUT_HALF && (
            <Picture
              imageClass="object-cover"
              alt={component.alt || ''}
              imageUrl={withStaticUrl(component.imageUri)}
            >
              <Source
                background={false}
                ratio={false}
                imageHeight={358}
                imageWidth={588}
              />
            </Picture>
          )}
          {component.layout === PRODUCT_BANNER_BOX_LAYOUT_FULL && (
            <Picture
              imageClass="object-cover"
              alt={component.alt || ''}
              imageUrl={withStaticUrl(component.imageUri)}
            >
              <Source
                background={false}
                ratio={false}
                imageWidth={2000}
                imageHeight={472}
                minScreenWidth={2000}
              />
              <Source
                background={false}
                ratio={false}
                imageWidth={1440}
                imageHeight={472}
                minScreenWidth={1440}
              />
              <Source
                background={false}
                ratio={false}
                imageWidth={1200}
                imageHeight={472}
                minScreenWidth={1200}
              />
              <Source
                background={false}
                ratio={false}
                imageWidth={768}
                imageHeight={472}
                minScreenWidth={768}
              />
              <Source
                background={false}
                ratio={false}
                imageWidth={768}
                imageHeight={361}
              />
            </Picture>
          )}
        </div>
        {component.text && (
          <div className={styles.ProductBannerBox__text}>
            <Markdown
              options={{ disableParsingRawHTML: true }}
              children={component.text}
            />
          </div>
        )}
        {component.url && (
          <a
            className={styles.ProductBannerBox_linked}
            aria-label="go to link"
            href={component.url}
          ></a>
        )}
      </div>
    </div>
  );
};

export default ProductBannerBox;
